import { useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { t } from 'services/translation';

import {
  DiscountContainer,
  DiscountHeader,
  DiscountIcon,
  IconLine,
  DiscountTitle,
  InputButtonWrapper,
  DiscountInput,
  ApplyButton,
  CodeWrapper,
  SuccessIcon,
  AppliedText,
  AppliedCode,
  RemoveButton,
  ErrorMessage,
} from './styles';

interface DiscountProps {
  additionalParams: any;
  errors?: any;
  discountApplied: boolean;
  discountLoading: boolean;
  discountCode?: string;
  onApplyDiscount: (params: any) => void;
  onRemoveDiscount: (params: any) => void;
}

export const Discount = ({
  additionalParams,
  errors,
  discountApplied,
  discountLoading,
  discountCode,
  onApplyDiscount,
  onRemoveDiscount,
}: DiscountProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [inputValue, setInputValue] = useState(discountCode || '');
  const [clientSideErrors, setClientSideErrors] = useState<string[]>([]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
    setClientSideErrors([]);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setClientSideErrors([]);
  };

  const handleApply = () => {
    const code = inputValue.trim();

    if (!code) {
      setClientSideErrors([t('CANT_BE_BLANK')]);
      return;
    }

    if (!discountLoading) {
      setClientSideErrors([]);
      onApplyDiscount({
        ...additionalParams,
        discount_code: code,
      });
    }
  };

  const handleRemove = () => {
    if (!discountLoading) {
      onRemoveDiscount(additionalParams);
    }
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleApply();
    }
  };

  const getErrorMessages = () => {
    if (clientSideErrors.length > 0) {
      return clientSideErrors;
    }
    if (errors && errors.toJS) {
      return errors.toJS();
    }
    return [];
  };

  const errorMessages = getErrorMessages();

  return (
    <DiscountContainer
      className={`DiscountCodeContainer ${isExpanded ? 'expanded' : ''}`}
    >
      {discountApplied ? (
        <div
          className="DiscountCode-applied"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <SuccessIcon className="icon af-checkmark brand-bg" />
          <AppliedText className="copy">{t('PROMO_CODE_APPLIED')}</AppliedText>
          <CodeWrapper className="code">
            <AppliedCode>{inputValue}</AppliedCode>
            <RemoveButton
              className="DiscountCode-remove brand-color"
              data-testid="discount-remove"
              onClick={handleRemove}
            >
              {t('REMOVE')}
            </RemoveButton>
          </CodeWrapper>
        </div>
      ) : (
        <>
          <DiscountHeader className="DiscountCode-Title">
            <DiscountIcon isExpanded={isExpanded} onClick={handleToggle}>
              <IconLine isExpanded={isExpanded} />
            </DiscountIcon>
            <DiscountTitle>{t('REDEEM_PROMO_CODE')}</DiscountTitle>
          </DiscountHeader>
          <div
            style={{
              display: isExpanded ? 'block' : 'none',
              marginTop: '12px',
            }}
          >
            <InputButtonWrapper
              className={`DiscountCode-Input row ${isExpanded ? 'expanded' : ''}`}
            >
              <DiscountInput
                data-testid="discount-input"
                type="text"
                placeholder={t('ENTER_PROMO_CODE')}
                value={inputValue}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                disabled={discountLoading}
                hasError={errorMessages.length > 0}
                className="DiscountCode-Input"
              />
              <ApplyButton
                data-testid="discount-apply"
                className={`ui ${discountLoading ? 'loading' : ''} button`}
                onClick={handleApply}
                isLoading={discountLoading}
                disabled={discountLoading}
              >
                {discountLoading ? t('APPLY') : t('APPLY')}
              </ApplyButton>
            </InputButtonWrapper>

            {errorMessages.length > 0 && (
              <ErrorMessage>{errorMessages.join(', ')}</ErrorMessage>
            )}
          </div>
        </>
      )}
    </DiscountContainer>
  );
};
